<template>
  <span>
    <div class="relative h-full">
      <div id="payment-element"></div>
      <ColorButton
        :text="$t('pay')"
        v-if="showPayButton"
        @click="stripeCheckout($event)"
        class="md:mt-8 sm:mt-4 forceWhite"
      ></ColorButton>
    </div>
  </span>
</template>
<script lang="ts">
import { defineComponent, ref } from "vue";
import loadScript from "load-script";
import { PaymentApi } from "@/service/PaymentApi";
import ColorButton from "@/components/buttons/colorButton.vue";
import { CreateStripePaymentIntentRequest } from "models/create-stripe-payment-intent-request";
import { GlobalErrors } from "@/enum/constants";
import { ErrorResponse } from "@/custom-models/errorResponse";
import { buildPaymentError } from "@/helpers/paymentErrorHelper";

export default defineComponent({
  name: "stripeForm",
  data() {
    return {
      token: "",
      error: "",
      stripe: {} as any,
      elements: {} as any,
      showPayButton: false,
      clientSecret: ""
    };
  },
  components: { ColorButton },
  props: ["data", "cardId", "user"],
  emits: ["paymentExecuted"],
  async mounted() {
    const payload:CreateStripePaymentIntentRequest = {
      saleId: this.data.id,
      currencyCode: this.data.amountData.currencyCode, 
      amount: this.data.amountData.grossAmount
    }
    const response = await PaymentApi.createStripePaymentIntent(payload);

    this.clientSecret = response.data.clientSecret;
    const targetElement: any = document.getElementById("payment-element");
    targetElement.innerHTML = "";

    await this.loadStripeScript();
    await this.loadStripeForm(this.clientSecret, this.data.paymentProviderData.properties.publicKey);
  },
  methods: {
    async loadStripeScript() {
      const scriptLoaded = ref(false);
      const scriptUrl = process.env.VUE_APP_STRIPE_INSITE_SCRIPT;

      await new Promise((resolve, reject) => {
        loadScript(scriptUrl, (err, script: any) => {
          if (err) {
            reject(err);
          } else {
            scriptLoaded.value = true;
            resolve(script);
          }
        });
      });
    },
    async loadStripeForm(clientSecret: string, publicKey: string) {
      const w: any = window;
      const Stripe = w.Stripe;
      this.stripe = Stripe(publicKey);
      const appearance = {
        theme: "stripe",
      };
      this.elements = this.stripe.elements({ appearance, clientSecret });
      const paymentElementOptions = {
        layout: "tabs",
      };
      const paymentElement = this.elements.create(
        "payment",
        paymentElementOptions
      );
      
      setTimeout(() => {
        paymentElement.mount("#payment-element");      
        paymentElement.on('change', (event: any) => {
          this.showPayButton = event.complete;
        });
      }, 1);
    },
    async stripeCheckout(e: any) {
      const url = new URL(window.location.href);
      url.searchParams.set('payment_redirect', 'stripe');
      url.searchParams.set('saleId', this.data.id);
      const fullUrl = url.toString();
      this.showPayButton = false;
      e.preventDefault();
      let error =null;
      if(this.data.amountData?.grossAmount ?? 0 > 0){
        const result = await this.stripe.confirmPayment({
          elements: this.elements,
          confirmParams: {
            return_url: fullUrl
          },
        });
        error = result.error;
      }
      else{
        //Si precio es 0
        const result = await this.stripe.confirmSetup({
          elements: this.elements,
          confirmParams: {
            return_url: fullUrl
          },
        });
        error = result.error;
      }
      
      // This point will only be reached if there is an immediate error when
      // confirming the payment. Otherwise, your customer will be redirected to
      // your `return_url`. For some payment methods like iDEAL, your customer will
      // be redirected to an intermediate site first to authorize the payment, then
      // redirected to the `return_url`.
      if (error.type === "card_error" || error.type === "validation_error") {
        const error: ErrorResponse = {
          timestamp: Date.now(),
          status: GlobalErrors.ERROR_401,
          message: this.$t("pay_declined") as string
        };
        const paymentError = buildPaymentError(error);
        return this.$emit("paymentExecuted", paymentError);
      } else {
        const error: ErrorResponse = {
          timestamp: Date.now(),
          status: GlobalErrors.ERROR_500,
          message: this.$t("payment_platform_operation_cannot_be_completed") as string
        };
        const paymentError = buildPaymentError(error);
        return this.$emit("paymentExecuted", paymentError);
      }
    },
  },
});
</script>
<style scoped>
#card-form {
  height: 169px !important;
}
@media (max-width: 500px) {
  #card-form {
    height: 200px !important;
  }
}
</style>